import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatPrice, primaryImage, titleize } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LayoutDashboard, Building2, Mail, Plus, TrendingUp, Eye, MessageSquare, Sparkles, Home as HomeIcon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export function OwnerLayout() {
    const { user, loading } = useAuth();
    const nav = useNavigate();

    useEffect(() => {
        if (!loading && !user) nav("/list-your-venue");
    }, [loading, user, nav]);

    if (loading || !user) return <div className="container-app py-24 text-center text-muted-foreground">Loading…</div>;

    const link = ({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${isActive ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`;

    return (
        <div className="container-app py-8 grid md:grid-cols-[240px_1fr] gap-8">
            <aside className="md:sticky md:top-24 md:self-start">
                <div className="rounded-2xl border border-border bg-card p-3">
                    <p className="px-2 py-1 text-xs uppercase tracking-widest text-muted-foreground">Owner</p>
                    <nav className="mt-1 flex flex-col gap-1">
                        <NavLink to="/dashboard" end className={link} data-testid="side-overview"><LayoutDashboard className="h-4 w-4" /> Overview</NavLink>
                        <NavLink to="/dashboard/venues" className={link} data-testid="side-venues"><Building2 className="h-4 w-4" /> My venues</NavLink>
                        <NavLink to="/dashboard/enquiries" className={link} data-testid="side-enquiries"><Mail className="h-4 w-4" /> Enquiries</NavLink>
                    </nav>
                </div>
                <Button asChild className="w-full mt-3 rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground" data-testid="side-add-venue">
                    <Link to="/dashboard/venue/new"><Plus className="h-4 w-4 mr-1" /> Add venue</Link>
                </Button>
            </aside>
            <div><Outlet /></div>
        </div>
    );
}

export function Overview() {
    const [stats, setStats] = useState({ venue_count: 0, total_views: 0, total_enquiries: 0, avg_completion_score: 0 });
    const [venues, setVenues] = useState([]);
    const [enquiries, setEnquiries] = useState([]);

    useEffect(() => {
        api.get("/owner/overview").then((r) => setStats(r.data));
        api.get("/owner/venues").then((r) => setVenues(r.data.items || []));
        api.get("/owner/enquiries").then((r) => setEnquiries(r.data.items || []));
    }, []);

    const cards = [
        { icon: Building2, label: "Venues", value: stats.venue_count },
        { icon: Eye, label: "Total views", value: stats.total_views },
        { icon: MessageSquare, label: "Enquiries", value: stats.total_enquiries },
        { icon: TrendingUp, label: "Profile score", value: `${stats.avg_completion_score}%` },
    ];

    return (
        <div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold" data-testid="owner-overview-h1">Dashboard</h1>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-6">
                {cards.map(({ icon: Icon, label, value }) => (
                    <div key={label} className="rounded-2xl border border-border bg-card p-5">
                        <Icon className="h-5 w-5 text-secondary" />
                        <p className="mt-3 text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
                        <p className="font-display text-3xl mt-1 font-semibold">{value}</p>
                    </div>
                ))}
            </div>

            <div className="mt-10 grid lg:grid-cols-[1fr_400px] gap-8">
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="font-display text-2xl font-semibold">Your venues</h2>
                        <Link to="/dashboard/venues" className="text-sm text-primary">View all →</Link>
                    </div>
                    <div className="space-y-3">
                        {venues.length === 0 ? (
                            <div className="rounded-xl border border-dashed border-border p-8 text-center">
                                <p className="font-display text-xl">No venues yet</p>
                                <p className="text-sm text-muted-foreground mt-2">Add your first venue to start receiving enquiries.</p>
                                <Button asChild className="mt-4 rounded-full"><Link to="/dashboard/venue/new"><Plus className="h-4 w-4 mr-1" /> Add venue</Link></Button>
                            </div>
                        ) : venues.slice(0, 4).map((v) => (
                            <div key={v.venue_id} className="flex items-center gap-4 rounded-xl border border-border bg-card p-3">
                                <img src={primaryImage(v)} alt="" className="h-16 w-24 rounded-lg object-cover" />
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate">{v.name}</p>
                                    <p className="text-xs text-muted-foreground truncate">{v.city} • {titleize(v.venue_type)}</p>
                                </div>
                                <StatusBadge status={v.status} />
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="font-display text-2xl font-semibold">Recent enquiries</h2>
                        <Link to="/dashboard/enquiries" className="text-sm text-primary">View all →</Link>
                    </div>
                    <div className="space-y-2">
                        {enquiries.length === 0 ? (
                            <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No enquiries yet</div>
                        ) : enquiries.slice(0, 5).map((e) => (
                            <div key={e.enquiry_id} className="rounded-xl border border-border bg-card p-3 text-sm">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <p className="font-semibold">{e.name}</p>
                                        <p className="text-xs text-muted-foreground">{e.venue_name} • {e.event_type ? titleize(e.event_type) : "General"}</p>
                                    </div>
                                    <Badge variant="outline" className="text-[10px] uppercase">{e.status}</Badge>
                                </div>
                                <p className="mt-2 text-muted-foreground line-clamp-2">{e.message}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        draft: "bg-muted text-muted-foreground",
        pending: "bg-yellow-100 text-yellow-800",
        published: "bg-green-100 text-green-800",
        rejected: "bg-red-100 text-red-800",
        suspended: "bg-red-100 text-red-800",
        archived: "bg-muted text-muted-foreground",
    };
    return <span className={`text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-1 ${map[status] || "bg-muted"}`}>{status}</span>;
}

export function MyVenues() {
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/owner/venues").then((r) => setItems(r.data.items || [])); }, []);
    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="font-display text-3xl font-semibold">My venues</h1>
                <Button asChild className="rounded-full bg-primary text-primary-foreground"><Link to="/dashboard/venue/new"><Plus className="h-4 w-4 mr-1" /> Add venue</Link></Button>
            </div>
            <div className="space-y-3">
                {items.length === 0 && (
                    <div className="rounded-xl border border-dashed border-border p-12 text-center">
                        <p className="font-display text-xl">Add your first venue</p>
                        <Button asChild className="mt-4 rounded-full"><Link to="/dashboard/venue/new"><Plus className="h-4 w-4 mr-1" /> Add venue</Link></Button>
                    </div>
                )}
                {items.map((v) => (
                    <div key={v.venue_id} className="rounded-xl border border-border bg-card p-4 grid md:grid-cols-[120px_1fr_auto] gap-4 items-center">
                        <img src={primaryImage(v)} alt="" className="h-20 w-full md:w-32 rounded-lg object-cover" />
                        <div>
                            <p className="font-display text-lg font-semibold">{v.name}</p>
                            <p className="text-sm text-muted-foreground">{v.city} • {titleize(v.venue_type)} • {formatPrice(v.price_from, v.price_currency)}</p>
                            <div className="mt-2 flex gap-2 items-center">
                                <StatusBadge status={v.status} />
                                <span className="text-xs text-muted-foreground">Profile score: {v.completion_score}%</span>
                            </div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <Button variant="outline" size="sm" onClick={() => nav("/dashboard/venue/new", { state: { editing: v } })} data-testid={`edit-venue-${v.venue_id}`}>Edit</Button>
                            {v.status === "published" && (
                                <Button asChild variant="ghost" size="sm">
                                    <Link to={`/venues/${v.country_slug}/${v.city_slug}/${v.slug}`} target="_blank">View</Link>
                                </Button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function Enquiries() {
    const [items, setItems] = useState([]);
    const refresh = () => api.get("/owner/enquiries").then((r) => setItems(r.data.items || []));
    useEffect(() => { refresh(); }, []);

    const updateStatus = async (id, status) => {
        await api.put(`/enquiries/${id}`, { status });
        toast.success("Updated");
        refresh();
    };

    return (
        <div>
            <h1 className="font-display text-3xl font-semibold mb-6">Enquiries</h1>
            <div className="space-y-3">
                {items.length === 0 && <div className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground">No enquiries yet.</div>}
                {items.map((e) => (
                    <div key={e.enquiry_id} className="rounded-xl border border-border bg-card p-5" data-testid={`enquiry-${e.enquiry_id}`}>
                        <div className="flex justify-between items-start flex-wrap gap-3">
                            <div>
                                <p className="font-display text-lg font-semibold">{e.name}</p>
                                <p className="text-sm text-muted-foreground">{e.email} {e.phone ? `• ${e.phone}` : ""}</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                    <b>{e.venue_name}</b> • {e.event_type ? titleize(e.event_type) : "-"} • {e.event_date || "Date TBC"} • {e.guests || "?"} guests {e.budget ? `• Budget ${e.budget}` : ""}
                                </p>
                            </div>
                            <Select value={e.status} onValueChange={(v) => updateStatus(e.enquiry_id, v)}>
                                <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="new">New</SelectItem>
                                    <SelectItem value="contacted">Contacted</SelectItem>
                                    <SelectItem value="won">Won</SelectItem>
                                    <SelectItem value="lost">Lost</SelectItem>
                                    <SelectItem value="archived">Archived</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <p className="mt-3 text-sm">{e.message}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}
